import React, { useState, useEffect } from 'react';
import { X, Download, Share, Plus } from 'lucide-react';
import { Button } from '@/components/ui/button';

const PWAInstallPrompt = () => {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    // Check if already installed (standalone mode)
    const standalone = window.matchMedia('(display-mode: standalone)').matches || 
                       window.navigator.standalone === true;
    setIsStandalone(standalone);

    // Check if iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(iOS);

    // Check if mobile
    const mobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    setIsMobile(mobile);

    // Check if dismissed recently (within 24 hours)
    const dismissedTime = localStorage.getItem('pwa-prompt-dismissed-time');
    if (dismissedTime) {
      const hoursSinceDismissed = (Date.now() - parseInt(dismissedTime)) / (1000 * 60 * 60);
      if (hoursSinceDismissed < 24) {
        return; // Don't show if dismissed within last 24 hours
      }
    }

    // Listen for the beforeinstallprompt event
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      
      // Show prompt immediately on mobile, after delay on desktop
      setTimeout(() => {
        setShowPrompt(true);
      }, mobile ? 1500 : 3000);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Show iOS instructions after delay if on iOS and not installed
    if (iOS && !standalone && mobile) {
      setTimeout(() => {
        setShowPrompt(true);
      }, 2000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;

    deferredPrompt.prompt();
    await deferredPrompt.userChoice;

    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    setShowPrompt(false);
    // Remember dismissal time
    localStorage.setItem('pwa-prompt-dismissed-time', Date.now().toString());
  };

  // Don't show if already installed
  if (isStandalone) {
    return null;
  }

  if (!showPrompt) return null;

  return (
    <>
      {/* Backdrop for mobile */}
      {isMobile && (
        <div 
          className="fixed inset-0 bg-black/60 z-40 md:hidden"
          onClick={handleDismiss}
        />
      )}
      
      {/* Install Prompt */}
      <div className={`fixed z-50 ${
        isMobile 
          ? 'bottom-0 left-0 right-0 rounded-t-2xl' 
          : 'bottom-24 left-4 right-4 md:left-auto md:right-4 md:w-80 rounded-lg'
      } bg-zinc-900 border-t-2 md:border border-crew-yellow shadow-2xl p-5 animate-in slide-in-from-bottom-5`}>
        
        <button 
          onClick={handleDismiss}
          className="absolute top-3 right-3 text-zinc-500 hover:text-white p-1"
          aria-label="Dismiss"
        >
          <X size={20} />
        </button>
        
        <div className="flex items-start gap-4">
          {/* App Icon */}
          <div className="bg-black p-3 rounded-xl border border-crew-yellow/30 flex-shrink-0">
            <img 
              src="/icons/icon-72x72.png" 
              alt="VERSO Crew" 
              className="w-12 h-12"
            />
          </div>
          
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-white text-lg mb-1">
              Install VERSO Crew
            </h3>
            
            {isIOS ? (
              <div className="text-sm text-zinc-300 mb-4">
                <p className="mb-2">Add this app to your home screen:</p>
                <div className="flex items-center gap-2 text-crew-yellow">
                  <span className="flex items-center gap-1">
                    1. Tap <Share className="w-4 h-4 inline" /> Share
                  </span>
                </div>
                <div className="flex items-center gap-2 text-crew-yellow mt-1">
                  <span className="flex items-center gap-1">
                    2. Tap <Plus className="w-4 h-4 inline" /> "Add to Home Screen"
                  </span>
                </div>
              </div>
            ) : (
              <p className="text-sm text-zinc-400 mb-4">
                Get quick access and work offline. Install now for the best experience!
              </p>
            )}
            
            {!isIOS && deferredPrompt && (
              <Button
                onClick={handleInstall}
                className="w-full bg-crew-yellow text-black font-bold hover:bg-white"
                data-testid="pwa-install-button"
              >
                <Download className="w-5 h-5 mr-2" />
                Add to Home Screen
              </Button>
            )}
            
            {isIOS && (
              <Button
                onClick={handleDismiss}
                variant="outline"
                className="w-full border-zinc-600 text-zinc-300"
              >
                Got it
              </Button>
            )}
          </div>
        </div>
        
        {/* Safe area padding for iOS */}
        {isMobile && <div className="h-4" />}
      </div>
    </>
  );
};

export default PWAInstallPrompt;
